import { ReactElement } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { Box, Checkbox, FormControlLabel, Stack, SxProps, Theme, Typography } from '@mui/material'
import { CentimeterField } from '@ui/forms'
import { SupportWidthTextIcon } from '@ui/icons/misc'
import { compactCheckboxSx, growFieldInnerSx, growFieldSx } from '../constants'

/**
 * Same fixed width for the lA and b labels so the two stacked fields left-align
 * despite the labels' different natural widths.
 */
const FIELD_LABEL_SX: SxProps<Theme> = { width: 20, flexShrink: 0 }

/**
 * Support area staged for every support, its two fields stacked to keep the
 * block narrow. Unchecked = no explicit area — each support keeps deriving its
 * own geometric value, which is deliberately not previewed here because it
 * differs per member. Checked = one explicit length/width for all supports.
 */
const SharedSupportAreaField = (): ReactElement => {
  const { setValue } = useFormContext()
  const area: RectangularSupportArea | null | undefined = useWatch({ name: 'sharedSettings.area' })

  return (
    <Stack direction="column" spacing={1}>
      <FormControlLabel
        control={
          <Checkbox
            size="small"
            sx={compactCheckboxSx}
            checked={area != null}
            data-cy="support-compression-area-checkbox"
            onChange={(_event, checked) =>
              setValue('sharedSettings.area', checked ? { length: 0.01, width: 0.01 } : null, {
                shouldDirty: true,
                shouldTouch: true,
              })
            }
          />
        }
        label="angeben"
        sx={{ margin: 0 }}
      />
      {area != null && (
        <Stack direction="column" spacing={1}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Box sx={FIELD_LABEL_SX}>
              <Typography fontSize="inherit">
                l<sub>A</sub>
              </Typography>
            </Box>
            <Box sx={growFieldSx(64)}>
              <CentimeterField
                name="sharedSettings.area.length"
                sx={growFieldInnerSx}
                data-cy="support-compression-area-length"
              />
            </Box>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <Box sx={FIELD_LABEL_SX}>
              <SupportWidthTextIcon />
            </Box>
            <Box sx={growFieldSx(64)}>
              <CentimeterField
                name="sharedSettings.area.width"
                sx={growFieldInnerSx}
                data-cy="support-compression-area-width"
              />
            </Box>
          </Stack>
        </Stack>
      )}
    </Stack>
  )
}

export default SharedSupportAreaField
